import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { ConfirmProvider } from "@/components/ConfirmDialog";
import Layout from "@/components/Layout";
import ClientLayout from "@/components/ClientLayout";
import LandingPage from "@/pages/LandingPage";
import LeadsPage from "@/pages/LeadsPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import AcceptInvitePage from "@/pages/AcceptInvitePage";
import ClientJoinPage from "@/pages/ClientJoinPage";
import DashboardPage from "@/pages/DashboardPage";
import ClientsPage from "@/pages/ClientsPage";
import ClientDetailPage from "@/pages/ClientDetailPage";
import CalendarPage from "@/pages/CalendarPage";
import PaymentsPage from "@/pages/PaymentsPage";
import SettingsPage from "@/pages/SettingsPage";
import ExercisesPage from "@/pages/ExercisesPage";
import ProgramsPage from "@/pages/ProgramsPage";
import ProgramBuilderPage from "@/pages/ProgramBuilderPage";
import GrowthPage from "@/pages/GrowthPage";
import CoachApplyPage from "@/pages/CoachApplyPage";
import MessagesPage from "@/pages/MessagesPage";
import MealPlansPage from "@/pages/MealPlansPage";
import MealPlanBuilderPage from "@/pages/MealPlanBuilderPage";
import IntervalTimerPage from "@/pages/IntervalTimerPage";
import PricingPage from "@/pages/PricingPage";
import ChallengePage from "@/pages/ChallengePage";
import CheckoutSuccessPage from "@/pages/CheckoutSuccessPage";
import ClientHomePage from "@/pages/client/ClientHomePage";
import ClientCalendarPage from "@/pages/client/ClientCalendarPage";
import ClientHabitsPage from "@/pages/client/ClientHabitsPage";
import ClientCheckInPage from "@/pages/client/ClientCheckInPage";
import ClientMessagesPage from "@/pages/client/ClientMessagesPage";
import ClientReferralsPage from "@/pages/client/ClientReferralsPage";
import ClientNutritionPage from "@/pages/client/ClientNutritionPage";
import ClientProgressPage from "@/pages/client/ClientProgressPage";
import ClientDocumentsPage from "@/pages/client/ClientDocumentsPage";
import DocumentsPage from "@/pages/DocumentsPage";
import DocumentTemplateBuilderPage from "@/pages/DocumentTemplateBuilderPage";
import DocumentSubmissionPage from "@/pages/DocumentSubmissionPage";
import NutritionSentPage from "@/pages/NutritionSentPage";

function Loading() {
    return (
        <div className="min-h-screen flex items-center justify-center bg-[var(--bg)]">
            <div className="font-mono text-xs tracking-[0.3em] text-[var(--text-muted)]">LOADING…</div>
        </div>
    );
}

function CoachOnly({ children }) {
    const { user, loading } = useAuth();
    if (loading || user === null) return <Loading />;
    if (!user) return <Navigate to="/login" replace />;
    if (user.role === "client") return <Navigate to="/client" replace />;
    return <Layout>{children}</Layout>;
}

function HomeRouter() {
    const { user, loading } = useAuth();
    if (loading || user === null) return <Loading />;
    if (user && user.role === "client") return <Navigate to="/client" replace />;
    if (user) return <Layout><DashboardPage /></Layout>;
    return <LandingPage />;
}

function ClientOnly({ children }) {
    const { user, loading } = useAuth();
    if (loading || user === null) return <Loading />;
    if (!user) return <Navigate to="/login" replace />;
    if (user.role !== "client") return <Navigate to="/" replace />;
    return <ClientLayout>{children}</ClientLayout>;
}

function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <AuthProvider>
                    <ConfirmProvider>
                        <Routes>
                        <Route path="/login" element={<LoginPage />} />
                        <Route path="/register" element={<RegisterPage />} />
                        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                        <Route path="/reset-password" element={<ResetPasswordPage />} />
                        <Route path="/accept-invite" element={<AcceptInvitePage />} />
                        <Route path="/join/:code" element={<ClientJoinPage />} />
                        <Route path="/apply-coach" element={<CoachApplyPage />} />
                        <Route path="/pricing" element={<PricingPage />} />
                        <Route path="/challenge" element={<ChallengePage />} />
                        <Route path="/checkout/success" element={<CheckoutSuccessPage />} />

                        {/* Public landing — smart redirects authed users to their dashboard */}
                        <Route path="/" element={<HomeRouter />} />

                        {/* Coach */}
                        <Route path="/dashboard" element={<CoachOnly><DashboardPage /></CoachOnly>} />
                        <Route path="/leads" element={<CoachOnly><LeadsPage /></CoachOnly>} />
                        <Route path="/clients" element={<CoachOnly><ClientsPage /></CoachOnly>} />
                        <Route path="/clients/:id" element={<CoachOnly><ClientDetailPage /></CoachOnly>} />
                        <Route path="/calendar" element={<CoachOnly><CalendarPage /></CoachOnly>} />
                        <Route path="/payments" element={<CoachOnly><PaymentsPage /></CoachOnly>} />
                        <Route path="/settings" element={<CoachOnly><SettingsPage /></CoachOnly>} />
                        <Route path="/exercises" element={<CoachOnly><ExercisesPage /></CoachOnly>} />
                        <Route path="/programs" element={<CoachOnly><ProgramsPage /></CoachOnly>} />
                        <Route path="/programs/:id" element={<CoachOnly><ProgramBuilderPage /></CoachOnly>} />
                        <Route path="/growth" element={<CoachOnly><GrowthPage /></CoachOnly>} />
                        <Route path="/messages" element={<CoachOnly><MessagesPage /></CoachOnly>} />
                        <Route path="/meal-plans" element={<CoachOnly><MealPlansPage /></CoachOnly>} />
                        <Route path="/meal-plans/:id" element={<CoachOnly><MealPlanBuilderPage /></CoachOnly>} />
                        <Route path="/timer" element={<CoachOnly><IntervalTimerPage /></CoachOnly>} />
                        <Route path="/nutrition-sent" element={<CoachOnly><NutritionSentPage /></CoachOnly>} />
                        <Route path="/documents" element={<CoachOnly><DocumentsPage /></CoachOnly>} />
                        <Route path="/documents/:id" element={<CoachOnly><DocumentTemplateBuilderPage /></CoachOnly>} />
                        <Route path="/documents/submission/:id" element={<CoachOnly><DocumentSubmissionPage /></CoachOnly>} />

                        {/* Client portal */}
                        <Route path="/client" element={<ClientOnly><ClientHomePage /></ClientOnly>} />
                        <Route path="/client/calendar" element={<ClientOnly><ClientCalendarPage /></ClientOnly>} />
                        <Route path="/client/habits" element={<ClientOnly><ClientHabitsPage /></ClientOnly>} />
                        <Route path="/client/check-in" element={<ClientOnly><ClientCheckInPage /></ClientOnly>} />
                        <Route path="/client/messages" element={<ClientOnly><ClientMessagesPage /></ClientOnly>} />
                        <Route path="/client/referrals" element={<ClientOnly><ClientReferralsPage /></ClientOnly>} />
                        <Route path="/client/nutrition" element={<ClientOnly><ClientNutritionPage /></ClientOnly>} />
                        <Route path="/client/progress" element={<ClientOnly><ClientProgressPage /></ClientOnly>} />
                        <Route path="/client/timer" element={<ClientOnly><IntervalTimerPage /></ClientOnly>} />
                        <Route path="/client/documents" element={<ClientOnly><ClientDocumentsPage /></ClientOnly>} />

                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                    </ConfirmProvider>
                </AuthProvider>
            </BrowserRouter>
        </div>
    );
}

export default App;
